// Tag matching shared by the header search, the mini search bar and the Search
// Console: a tag matches when its NAME STARTS WITH what was typed — "mi" finds
// milf and miku_hatsune, not hatsune_miku (which starts with "ha"). Booru tags
// use underscores, so spaces and underscores are treated as the same character.
//
// (VisiSearch has its own matcher in lib/visisearch-search.ts, which works on a
// local list and adds a typo-tolerant fallback; the sites behind these
// suggestions send back a short, popularity-ranked list, which is kept.)

const key = (value: string) => value.toLowerCase().trim().replace(/[\s_]+/g, "_")

// A leading "-" marks a negative tag in the search bar; it isn't part of the name.
const bare = (value: string) => value.replace(/^-/, "")

/** True when `tag` starts with `query` (an empty query matches everything). */
export function tagStartsWith(query: string, tag: string): boolean {
  const q = key(bare(query))
  return !q || key(bare(tag)).startsWith(q)
}

/**
 * Orders tag suggestions the way the booru sites rank them: an exact match first, then tags that START with what was
 * typed, then the rest, each group keeping the order the site sent (its popularity ranking). "fee" gives feet
 * (276k posts) before feeding (12k), and "kimo" gives kimono before white_kimono, as on Danbooru itself. Matches that
 * only contain the text (bare_feet for "fee") are kept, after the prefix matches, because a site only returns what it
 * considers relevant.
 */
export function orderTagSuggestions(query: string, suggestions: string[], max = 15): string[] {
  const q = key(bare(query))
  if (!q) return suggestions.slice(0, max)

  const seen = new Set<string>()
  const exact: string[] = []
  const prefix: string[] = []
  const other: string[] = []
  for (const suggestion of suggestions) {
    const k = key(bare(suggestion))
    if (seen.has(k)) continue
    seen.add(k)
    if (k === q) exact.push(suggestion)
    else if (k.startsWith(q)) prefix.push(suggestion)
    else other.push(suggestion)
  }

  return [...exact, ...prefix, ...other].slice(0, max)
}

/** A post count the way the booru sites show it: 8.0k, 276k, 1.2M. */
export function formatPostCount(count: number): string {
  if (!Number.isFinite(count) || count < 0) return ""
  if (count < 1000) return String(Math.round(count))
  if (count < 100_000) return `${(count / 1000).toFixed(1)}k`
  if (count < 1_000_000) return `${Math.round(count / 1000)}k`
  return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`
}
